import React from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  Cog6ToothIcon,
  CurrencyPoundIcon,
  HandRaisedIcon,
  XMarkIcon,
  UserGroupIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  ArrowPathRoundedSquareIcon,
  SwatchIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

export default function OvertimeManagerHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ClockIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Overtime Management</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive overtime request and approval system for managing additional staffing needs,
            budget tracking, and automated bidding processes.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Overtime Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <HandRaisedIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Staff Bidding</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Automated bidding system where staff can express interest in overtime opportunities
                  based on their availability and preferences.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <CurrencyPoundIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Budget Management</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Real-time budget tracking and cost analysis to ensure overtime expenses
                  stay within allocated departmental budgets.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Request Approval</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Streamlined approval workflow for overtime requests with automated
                  budget validation and staff availability checking.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Analytics Dashboard</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Comprehensive analytics showing overtime trends, cost analysis,
                  and staff participation metrics for data-driven decisions.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Button Layouts Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Tab Navigation Layout */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-white mb-4">Navigation Tabs</h3>
              <div className="flex flex-wrap gap-3 mb-6">
                <button className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 opacity-75 cursor-not-allowed">
                  <HandRaisedIcon className="h-5 w-5" />
                  <span>Bids</span>
                </button>

                <button className="bg-sky-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2 opacity-75 cursor-not-allowed">
                  <CheckBadgeIcon className="h-5 w-5" />
                  <span>Tasks</span>
                </button>

                <button className="bg-sky-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2 opacity-75 cursor-not-allowed">
                  <ClockIcon className="h-5 w-5" />
                  <span>Attendance</span>
                </button>
              </div>
              <p className="text-gray-300 text-sm">Switch between different overtime management views</p>
            </div>

            {/* Overtime Requests Layout */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Overtime Requests Management</h3>

              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button className="bg-yellow-600 text-white px-4 py-2 rounded-lg opacity-75 cursor-not-allowed">
                  Pending Requests
                </button>

                <button className="bg-green-600 text-white px-4 py-2 rounded-lg opacity-75 cursor-not-allowed">
                  Approved Requests
                </button>

                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg opacity-75 cursor-not-allowed">
                  Accepted Requests
                </button>
              </div>

              {/* Sample Request Card */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 mb-4 border border-yellow-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">John Smith - Cashier</h4>
                    <p className="text-sm text-gray-600">October 15, 2025 • 2 Hours • £30.00</p>
                  </div>
                  <span className="px-3 py-1 bg-yellow-500 text-white rounded-full text-sm">Pending</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button className="bg-green-600 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                    Approve
                  </button>

                  <button className="bg-red-600 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                    Reject
                  </button>

                  <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                    Amend Hours
                  </button>
                </div>
              </div>

              {/* Compare Button */}
              <div className="flex justify-center">
                <button className="bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center space-x-2 opacity-75 cursor-not-allowed">
                  <ChartBarIcon className="h-5 w-5" />
                  <span>Compare & Recommend</span>
                </button>
              </div>
            </div>

            {/* Bids Management Layout */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 mb-8 border border-purple-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Staff Bidding System</h3>

              {/* Bid Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                  <h4 className="font-semibold text-gray-900">Sarah Johnson</h4>
                  <p className="text-sm text-gray-600">Bids: 5/8 (62.5%)</p>
                  <p className="text-sm text-gray-600">Rejected: 2</p>
                </div>

                <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                  <h4 className="font-semibold text-gray-900">Mike Davis</h4>
                  <p className="text-sm text-gray-600">Bids: 3/4 (75%)</p>
                  <p className="text-sm text-gray-600">Rejected: 1</p>
                </div>

                <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                  <h4 className="font-semibold text-gray-900">Emma Wilson</h4>
                  <p className="text-sm text-gray-600">Bids: 7/9 (78%)</p>
                  <p className="text-sm text-gray-600">Rejected: 0</p>
                </div>
              </div>

              {/* Sample Overtime Opportunity */}
              <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">Cashier Position</h4>
                    <p className="text-sm text-gray-600">October 16, 2025 • 3 Hours Available</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
                    <span className="text-sm text-gray-600">Bids</span>
                  </div>
                </div>

                {/* Sample Bids */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-gray-50 rounded p-3 border border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">John Smith</p>
                      <p className="text-sm text-gray-600">Bid: 2 Hours • Submitted: 2 hours ago</p>
                    </div>
                    <div className="flex space-x-2">
                      <button className="bg-green-600 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                        Accept
                      </button>

                      <button className="bg-yellow-500 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                        Split Hours
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between bg-gray-50 rounded p-3 border border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Sarah Johnson</p>
                      <p className="text-sm text-gray-600">Bid: 1 Hour • Submitted: 1 hour ago</p>
                    </div>
                    <div className="flex space-x-2">
                      <button className="bg-green-600 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                        Accept
                      </button>

                      <button className="bg-yellow-500 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                        Split Hours
                      </button>
                    </div>
                  </div>
                </div>

                {/* Auction Controls */}
                <div className="flex justify-center space-x-3 mt-4">
                  <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg opacity-75 cursor-not-allowed">
                    Close Auction
                  </button>

                  <button className="bg-red-600 text-white px-4 py-2 rounded-lg opacity-75 cursor-not-allowed">
                    Cancel Overtime
                  </button>

                  <button className="bg-red-700 text-white px-4 py-2 rounded-lg opacity-75 cursor-not-allowed">
                    Emergency Cancel
                  </button>
                </div>
              </div>
            </div>

            {/* Budget Table Layout */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Budget Management Dashboard</h3>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-gray-300">
                        Date
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-gray-300">
                        Budget
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-gray-300">
                        Cashier Cost
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-gray-300">
                        Cashier OT Hours
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr className="border-b border-gray-200">
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">15/10/25</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">£200.00</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">£120.00</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">2</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm border-l border-gray-200">
                        <button className="bg-indigo-600 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                          Request OT
                        </button>
                      </td>
                    </tr>

                    <tr className="border-b border-gray-200">
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">16/10/25</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">£200.00</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">£90.00</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 border-r border-gray-200">0</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm border-l border-gray-200">
                        <button className="bg-indigo-600 text-white px-3 py-1 rounded text-sm opacity-75 cursor-not-allowed">
                          Request OT
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Tab Navigation</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Switch between different overtime management views: Bids for staff bidding,
                  Tasks for approved overtime, and Attendance for time tracking.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Request Filtering</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Filter overtime requests by status: pending approval, approved but not accepted,
                  or fully accepted and scheduled for work.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Smart Recommendations</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  AI-powered comparison of multiple requests to recommend the most cost-effective
                  and efficient overtime assignments within budget constraints.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Bid Statistics</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Track staff bidding patterns and acceptance rates to identify reliable overtime
                  candidates and optimize future bidding processes.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Auction Management</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Control overtime bidding periods with options to close auctions, cancel opportunities,
                  or handle emergency cancellations when needed.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Budget Tracking</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Real-time budget monitoring with automatic cost calculations and overtime hour
                  tracking to prevent budget overruns.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Overtime Management Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Request Management</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Review budget availability before approving requests</li>
                    <li>• Compare multiple requests using the recommendation system</li>
                    <li>• Consider staff workload and availability patterns</li>
                    <li>• Amend hours when partial coverage is sufficient</li>
                    <li>• Monitor approval rates and adjust thresholds as needed</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Bidding Strategy</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Set clear bidding windows to ensure fair participation</li>
                    <li>• Monitor bid acceptance rates for staff performance</li>
                    <li>• Use split hours feature for optimal coverage</li>
                    <li>• Close auctions promptly to maintain staff engagement</li>
                    <li>• Track bidding patterns to identify reliable candidates</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Budget Control</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Set realistic budgets based on business needs</li>
                    <li>• Monitor cost trends and adjust budgets proactively</li>
                    <li>• Use the budget table for capacity planning</li>
                    <li>• Consider overtime costs vs. hiring additional staff</li>
                    <li>• Generate regular budget reports for management</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Staff Relations</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Communicate overtime opportunities clearly and fairly</li>
                    <li>• Provide feedback on bid acceptance/rejection</li>
                    <li>• Recognize staff who frequently accept overtime</li>
                    <li>• Balance overtime distribution across team members</li>
                    <li>• Respect staff preferences and availability</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}